// ═══════════════════════════════════════════════════════════════════════════
// Classification Mapping Editor
// ═══════════════════════════════════════════════════════════════════════════
//
// Shared between file uploads (CampaignsTab's UploadPanel) and API source
// creation (APISourcesTab's CreateModal when sourceFilter='campaign').
//
// Shows the detected intent with the ability to flip between response /
// conversion, plus role dropdowns pre-filled from auto_detect_intent.
//
// Lives in its own module because both CampaignsTab and APISourcesTab need
// it, and CampaignsTab already imports CreateModal from APISourcesTab —
// placing the editor in either of those files creates a circular import.

import React, { useState } from 'react';

export const CAMPAIGN_ROLES = [
  { key: 'customer_key',    label: 'Customer key',    required: true,
    hint: 'How this channel identifies the customer — email, phone, or internal ID' },
  { key: 'event_type',      label: 'Event type',      required: true,
    hint: 'The action name: delivered, opened, clicked, purchased, redeemed…' },
  { key: 'event_timestamp', label: 'Event timestamp', required: true,
    hint: 'When the event happened. Parsed with best-effort; ISO-8601 preferred' },
  { key: 'event_value',     label: 'Event value',     required: false,
    hint: 'Numeric revenue / score / amount. Leave blank for response events' },
  { key: 'conversion_flag', label: 'Conversion flag', required: false,
    hint: 'Column whose values indicate a conversion. Can be a boolean flag column, or the event_type column itself — values like converted/purchased/enrolled are recognised automatically.' },
];

export const ClassificationMappingEditor = ({
  filename,
  columns,
  sampleRows,
  detected,
  onCancel,
  onConfirm,
  mode = 'file',   // 'file' | 'api' — affects header copy only
}) => {
  const [intent, setIntent] = useState(detected?.intent || 'campaign_response');
  const [roles, setRoles] = useState(() => ({
    customer_key:    detected?.column_roles?.customer_key    || '',
    event_type:      detected?.column_roles?.event_type      || '',
    event_timestamp: detected?.column_roles?.event_timestamp || '',
    event_value:     detected?.column_roles?.event_value     || '',
    conversion_flag: detected?.column_roles?.conversion_flag || '',
  }));

  const confidence = Math.round((detected?.confidence || 0) * 100);
  const matched = detected?.matched_columns || [];
  const suggestedMetrics = detected?.suggested_metrics || [];
  // Map of role -> derivation metadata from the backend.
  // Shape: { conversion_flag: { column, derivation, matched_values } }
  const derivations = detected?.role_derivations || {};

  const requiredFilled = ['customer_key', 'event_type', 'event_timestamp']
    .every(k => roles[k] && roles[k].length > 0);

  const columnOptions = ['', ...columns];

  // Preview: find up to 3 sample values for each mapped role
  const preview = (col) => {
    if (!col || !sampleRows?.length) return null;
    const vals = sampleRows
      .map(r => r[col])
      .filter(v => v != null && String(v).length > 0)
      .slice(0, 3);
    return vals.length > 0 ? vals.join(' · ') : null;
  };

  const handleSubmit = () => {
    if (!requiredFilled) return;
    const column_roles = Object.fromEntries(
      Object.entries(roles).filter(([_, v]) => v && v.length > 0)
    );
    onConfirm({
      intent,
      column_roles,
      metric_definitions: suggestedMetrics,
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-gray-900">
            Confirm classification
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 truncate max-w-md">
            {mode === 'api' ? 'API source' : 'File'} · {filename}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-gray-400">auto-detect confidence</div>
          <div className={`text-sm font-medium ${
            confidence >= 70 ? 'text-emerald-600' :
            confidence >= 40 ? 'text-amber-600' : 'text-gray-500'
          }`}>
            {confidence}%
          </div>
        </div>
      </div>

      {/* Intent selector */}
      <div className="mb-4">
        <div className="text-[11px] text-gray-500 mb-1.5">Data intent</div>
        <div className="flex gap-2">
          {[
            { v: 'campaign_response',   l: 'Response',   d: 'Delivery, opens, clicks, replies' },
            { v: 'campaign_conversion', l: 'Conversion', d: 'Purchases, signups, redemptions' },
          ].map(opt => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setIntent(opt.v)}
              className={`flex-1 px-3 py-2 rounded border text-left transition-colors ${
                intent === opt.v
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <div className="text-xs font-medium text-gray-900">{opt.l}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{opt.d}</div>
            </button>
          ))}
        </div>
        {matched.length > 0 && (
          <div className="text-[10px] text-gray-400 mt-1.5">
            matched on: {matched.slice(0, 4).join(', ')}{matched.length > 4 ? '…' : ''}
          </div>
        )}
      </div>

      {/* Role mappings */}
      <div className="space-y-2.5 mb-4">
        <div className="text-[11px] text-gray-500">Column mappings</div>
        {CAMPAIGN_ROLES.map(role => {
          const previewVal = preview(roles[role.key]);
          const deriv = derivations[role.key];
          // The derivation is only meaningful while the user keeps the
          // auto-suggested column — if they pick a different column, the
          // derivation no longer applies.
          const derivActive = deriv && deriv.column === roles[role.key];
          return (
            <div key={role.key} className="flex items-start gap-3">
              <div className="w-36 pt-1.5">
                <div className="text-xs text-gray-900 flex items-center gap-1 flex-wrap">
                  {role.label}
                  {role.required && <span className="text-red-500 ml-0.5">*</span>}
                  {derivActive && deriv.derivation === 'event_type_value' && (
                    <span
                      className="text-[9px] px-1 py-0.5 bg-violet-100 text-violet-700 rounded"
                      title={`Derived from values in ${deriv.column}: ${(deriv.matched_values || []).join(', ')}`}
                    >
                      derived
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-gray-400 leading-tight">{role.hint}</div>
              </div>
              <div className="flex-1">
                <select
                  value={roles[role.key]}
                  onChange={(e) => setRoles(prev => ({ ...prev, [role.key]: e.target.value }))}
                  className={`w-full px-2.5 py-1.5 text-xs border rounded ${
                    role.required && !roles[role.key]
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {columnOptions.map(c => (
                    <option key={c} value={c}>{c || '— not mapped —'}</option>
                  ))}
                </select>
                {derivActive && deriv.derivation === 'event_type_value' && (
                  <div className="text-[10px] text-violet-700 mt-0.5">
                    values treated as conversions:{' '}
                    <span className="font-mono">
                      {(deriv.matched_values || []).slice(0, 5).join(', ')}
                      {(deriv.matched_values || []).length > 5 ? '…' : ''}
                    </span>
                  </div>
                )}
                {previewVal && !derivActive && (
                  <div className="text-[10px] text-gray-400 mt-0.5 truncate">
                    sample: {previewVal}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Explainer */}
      <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 mb-4">
        <div className="text-[10px] text-gray-600 leading-relaxed">
          These mappings project the wide {mode === 'api' ? 'payload' : 'sheet'}'s columns into
          narrow event rows in <code className="text-[10px] bg-white px-1 rounded">cdp_campaign_events</code>.
          {mode === 'file'
            ? ' The raw rows stay in a transient staging table and are dropped after extraction —'
            : ' Each webhook call projects its payload the same way —'}
          they never enter the customer profile table.
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!requiredFilled}
          className={`px-4 py-1.5 text-xs font-medium rounded ${
            requiredFilled
              ? 'bg-gray-900 text-white hover:bg-gray-800'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {mode === 'api' ? 'Create API source' : 'Classify & upload'}
        </button>
      </div>
    </div>
  );
};

export default ClassificationMappingEditor;
